import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { ExclamationTriangleIcon as OutlineExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import _ from "lodash";

import Modal from "src/components/Modal";
import { paymentHandlingAction, renewalHandlingActionName } from "../import";
import { IpRightImportStatusInfo, isImported } from "../state/modern_states";
import { useRenewalOverview } from "../interfaces/Renwals";
import { Link, useNavigate } from "src/navigation";
import { entity_sizes, PRIpRight, PrPostIp, purePostIp, useInstruction, useIpContext, useIpRightImport } from "./patent_renewals";
import { Field, Form, Formik } from "formik";
import { CostCenterSelect } from "./CostCenterSelect";
import { IpMetaContextProvider } from "./IpMeta";
import { IconSpinner } from "src/components/icons";
import { DatePicker } from "src/components/input/DatePicker";

export function PRImportMember() {

  // TODO: check if you have at least owner and cost center?
  // TODO: check if you have all the necessary information

  const { member, ipRightImportStatus } = useRenewalOverview()

  if (!member) return null

  if (ipRightImportStatus.substatus === 'missing-data') {
    return <NotEnoughDataModal {...{status: ipRightImportStatus}} />
  }

  // console.log({ipRightImportStatus})

  return (
    <IpMetaContextProvider>
      <ImportModalReal />
    </IpMetaContextProvider>
  ) 
}

function ImportModalReal() {
  const {t} = useTranslation()

  const navigate = useNavigate()
  const { member, ipRightImportStatus, actualIpRight: _prIpRight, ipRight } = useRenewalOverview()
  const prIpRight = _prIpRight as PRIpRight

  const {owners, costCenters} = useIpContext()

  const imported = isImported(ipRightImportStatus) || ipRight !== undefined
  const action = paymentHandlingAction({ status: ipRightImportStatus, imported })

  const { postIpRight, deriveInitial } = useIpRightImport()
  const {instruct} = useInstruction({ip_id: ipRight?.id})

  // console.log({member, costCenters, owners})

  const canStop = imported && ipRightImportStatus.substatus !== 'stopped' 

  //const cost_center_id = ipRight && ipRight.costCenters ? Object.keys(ipRight.costCenters)[0] : null
  const initialValues = prIpRight || {...deriveInitial(member) }//, cost_center_id }

  async function onSubmit(values: PrPostIp) {
    if (ipRight) {
      let hasChanged = false
      const toUpdate = purePostIp(values)
      // unset fields if they were already set
      // application_number, filing_date, grant_date, grant_number, publication_number"
      for (const field of ['application_number', 'filing_date', 'grant_date', 'grant_number', 'publication_number']) {
        if (prIpRight[field]) {
          toUpdate[field] = undefined
        }
      }
      if (prIpRight.application_number) {
        toUpdate
      }
      if (prIpRight.claim_count !== member.numberClaims) {
        // update PR IP Right with number of claims
        toUpdate['claim_count'] = member.numberClaims
        hasChanged = true
        // @ts-ignore
        //await postIpRight({...prIpRight, claim_count: member.numberClaims})
      }
      if (values.entity_size !== prIpRight.entity_size) {
        //toUpdate['entity_size'] = values.entity_size
        hasChanged = true
      }
      //console.log({hasChanged, toUpdate, prIpRight})
      if (hasChanged) {
        await postIpRight(toUpdate)
      }
      if (prIpRight.instruction !== 'Pay') {
        // reactivate
        await instruct(undefined, {instruction: 'Pay'})
      }
    } else {
      //console.log({values})
      await postIpRight(values)
    }
    navigate("../..")
  }

  function validate(values: PrPostIp) {
    const error = {} as Record<string, string>
    if (!(typeof values.cost_center_id === 'string')) {
      error['cost_center_id'] = 'required'
    }
    return error
  }

  return (
    <Modal overflowHidden={false}>
      <Formik {...{ initialValues, onSubmit, validate }}>{({errors, isSubmitting}) => {
        // console.log({values})
        const reqcc = (errors['cost_center_id'] || '') as string
        return <Form>
          <div className="p-4 space-y-2">
            <h2>
              {member.internalReference}: {t('payment-handling')}
            </h2>
            <div className="h-2" />

            <label htmlFor="customer_estimated_next_renewal_date" className="label">{t('first-due-date-with-payment-provider')}</label>
            <Field name="customer_estimated_next_renewal_date" as={DatePicker} />

            {member.countryCode === 'US' && <>
              <div className="h-2" />

              <label htmlFor="entity_size" className="label">{t('entitySize')}</label>
              <Field name="entity_size" className="form-select" as="select">
                {entity_sizes.map(es => <option value={es} key={es}>{t(`entity.${es}`)}</option>)}
              </Field>
            </>}

            <div className="h-2" />

            <label htmlFor="cost_center_id" className="label">{t('cost-center')} {reqcc && <span className="text-warn-700">*{t(reqcc)}</span>}</label>
            <Field name="cost_center_id" as={CostCenterSelect} className="min-w-lg" required {...{ owners, costCenters }} />

            <div className="h-2" />

            {member.countryCode === 'VT' && 
              <div>Make sure that you use the number of independent claims ({member.numberClaims})!</div>}

          </div>
          <div className="p-4 flex flex-row-reverse gap-4 bg-pcx-200 rounded-b-lg w-full">
            <button className="btn-primary" type="submit" disabled={isSubmitting || reqcc !== ''}>
              {t(renewalHandlingActionName(action))} {isSubmitting && <IconSpinner className="inline w-5 h-5 animate-spin" />}
            </button>
            <Link to="../.." className="btn-secondary">
              {t('cancel')}
            </Link>
            {canStop && <>
              <div className="grow max-sm:hidden" />
              <StopIpRightButton />
            </>
            }
          </div>
        </Form>
      }}</Formik>
    </Modal>
  )
}


function NotEnoughDataModal({status}: {status: IpRightImportStatusInfo}) {
    const {t} = useTranslation()

    const canStop = status.status === 'ready'

    const missingInformation = status.messages

    if (!missingInformation) return null

    const translated = _(missingInformation).map(missing => t(missing)).sortBy().value()

    return (
        <Modal>
            <div className='p-4'>
                <h2 className='mb-4'>{t("missing-information")}</h2>
                <p className='sm:text-lg text-gray-800'>{t("missing-information-no-registration")}</p>
                <div className='flex flex-row gap-4 items-center'>
                    <div className='max-sm:hidden px-4 text-warn-600'><OutlineExclamationTriangleIcon className='h-12 w-12' /></div>
                    <ul className='leading-relaxed text-lg lg:columns-xs lg:max-h-32'>
                        {translated.map(missing => <li key={missing}>{missing}</li>)}
                    </ul>
                </div>
                <p className='mt-4 sm:text-lg text-gray-800'>{t("edit-case-complete-info")}</p>
            </div>
            <div className='p-4 bg-pcx-200 flex flex-col sm:flex-row-reverse gap-4'>
                <Link className='btn-primary max-sm:text-center' to={"../../edit"}>{t('edit')}</Link>
                <Link className='btn-secondary max-sm:text-center' to={".."}>{t('back')}</Link>

                <div className="grow max-sm:hidden" />
                {canStop && <StopIpRightButton />}
            </div>
        </Modal>
    )
}

function StopIpRightButton() {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { stopIpRight } = useIpRightImport()

  const { member, ipRight, renewals } = useRenewalOverview()
  const ipRightId = ipRight.id
  const openRenewals = renewals.filter(r => r.state === 'Open')
  // console.log({renewals, openRenewals})

  const [isOpen, setIsOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function doStop() {
    if (!isSubmitting) {
      setIsSubmitting(true)
      await stopIpRight(ipRightId)
      setIsSubmitting(false)
      navigate('../..')
    }
  }

  return (
    <>
      <button type="button" className="btn-warn" onClick={() => setIsOpen(true)}>
        {t('stop-payment-handling')}
      </button>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
          {/* Background */}
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 backdrop-filter backdrop-blur-sm bg-black/25" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-lg transform overflow-hidden rounded-sm bg-white text-left align-middle shadow-lg transition-all">
                  <DialogTitle as="h3" className="p-4">
                    {member.internalReference}: {t('renewals-desc.stop')}
                  </DialogTitle>
                  <div className="p-4 pt-0">
                    <p className="font-base text-gray-500">{t('renewals-desc.really-stop')}</p>
                  </div>
                  {openRenewals.length > 0 && (
                    <div className="px-4 pb-4">
                      <div className="bg-warn-200 rounded px-4 py-2 text-warn-900 flex flex-row gap-4 items-center">
                        <ExclamationTriangleIcon className="w-8 h-8 inline-block text-warn-500" />
                        <div>
                          <p>If you stop the payment handling, the following fee payments will be cancelled</p>
                          <ul>
                            {openRenewals.map(renewal => (
                              <li key={renewal.id}>
                                {renewal.annuity && `Annuity ${renewal.annuity}; `}Due: {renewal.instructionDueDate}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* {!alreayStoppedWithPc && (
                    <label className="flex flex-row items-center gap-2 px-4">
                      <input type="checkbox" checked={alsoStopWithPc} onChange={() => setAlsoStopWithPc(!alsoStopWithPc)} />
                      <span className="text-gray-500">{t('stop-pc-case')}?</span>
                    </label>
                  )} */}

                  <div className="mt-4 p-4 bg-pcx-200 flex flex-row-reverse gap-2">
                    <button type="button" className="btn-warn flex flex-row gap-1 items-center" onClick={() => doStop()}>
                      {t('stop')} {isSubmitting && <IconSpinner className="inline w-5 h-5 animate-spin" />}
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setIsOpen(false)}>
                      {t('back')}
                    </button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}
